
import './App.css';
import React, { useEffect } from "react";
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Chip from '@mui/material/Chip';
import CardActionArea from '@mui/material/CardActionArea';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { SITE_ORIGIN, usePageSeo } from './usePageSeo';
import PrivacyPolicy from './PrivacyPolicy';
import WhiteNoisePrivacyPolicy from './WhiteNoisePrivacyPolicy';
import WhiteNoiseTerms from './WhiteNoiseTerms';
import Hush from './Hush';
import TimeDraw from './TimeDraw';
import QuickMenuCalendar from './QuickMenuCalendar';
import QuickMenuCalendarPrivacyPolicy from './QuickMenuCalendarPrivacyPolicy';
import QuickMenuCalendarTerms from './QuickMenuCalendarTerms';
import TimeDrawPrivacyPolicy from './TimeDrawPrivacyPolicy';
import BabySoundboard from './BabySoundboard';
import BabySoundboardPrivacyPolicy from './BabySoundboardPrivacyPolicy';
import CalendlyInlineWidget from './CalendlyInlineWidget';
import { analytics, logEvent } from './firebase';
import { 
  personalInfo, 
  navigationItems, 
  combinedProjects, 
  services,
  experience,
  siteConfig, 
  footerInfo
} from './data/portfolioData';

const drawerWidth = siteConfig.drawer.width;

const homeApps = [
  { name: 'White Noise: Premium Serenity', os: 'iOS', category: 'HealthApplication', url: `${SITE_ORIGIN}/white-noise`, description: 'A white noise mixer for sleep and focus, with layered sounds and a sleep timer.' },
  { name: 'Baby Soundboard & Toddler Games', os: 'iOS', category: 'EducationalApplication', url: `${SITE_ORIGIN}/baby-soundboard`, description: 'A free soundboard and toddler games app for babies and young children. No ads, no accounts, and no data collected.' },
  { name: 'Tuner Gauge: Chromatic Tuner', os: 'iOS', category: 'UtilitiesApplication', url: 'https://apps.apple.com/us/app/tuner-gauge-chromatic-tuner/id6751610479', description: 'A precision tuning utility with cent-level accuracy.' },
  { name: 'TimeDraw', os: 'iOS', category: 'LifestyleApplication', url: 'https://apps.apple.com/us/app/timedraw/id1603772416', description: 'A circular calendar drawn around an analog clockface.' },
  { name: 'iTello', os: 'iOS', category: 'UtilitiesApplication', url: 'https://apps.apple.com/us/app/itello/id1601051980', description: 'An iOS controller app for the DJI Tello drone.' },
  { name: 'File Crypt: Secure AES Browser', os: 'iOS', category: 'UtilitiesApplication', url: 'https://apps.apple.com/us/app/file-crypt-secure-aes-browser/id6621263965', description: 'An AES file encryption utility for iOS.' },
  { name: 'Menu Calendar Quick View', os: 'macOS', category: 'UtilitiesApplication', url: `${SITE_ORIGIN}/MenuCalendarQuickView`, description: 'A Mac menu bar calendar for the month, events, and reminders.' }
];

function HomeRoute({ children }) {
  usePageSeo({
    title: siteConfig.title,
    description: siteConfig.description,
    path: '/',
    image: `${SITE_ORIGIN}/portrait.jpg`,
    keywords: 'Michael Ellis, experienced iOS developer, iOS software engineer, Swift, SwiftUI, ships iOS apps, iPhone app developer, Mac developer',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Hobby apps published by Michael Ellis, iOS software engineer',
      itemListElement: homeApps.map((app, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'SoftwareApplication',
          name: app.name,
          operatingSystem: app.os,
          applicationCategory: app.category,
          url: app.url,
          description: app.description,
          author: {
            '@type': 'Person',
            name: 'Michael R. Ellis',
            jobTitle: 'iOS Software Engineer',
            url: `${SITE_ORIGIN}/`
          }
        }
      }))
    }
  });
  return children;
}

function App() {
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Typography variant="h6" sx={{ 
        my: 2,
        color: '#2c3e50',
        '@media (prefers-color-scheme: dark)': {
          color: '#ffffff'
        }
      }}>
        {siteConfig.drawer.title}
      </Typography>
      <Divider />
      <List>
        {navigationItems.map((item) => (
          <ListItem key={item.name} disablePadding>
            <ListItemButton sx={{ textAlign: 'center' }} component="a" href={item.link}>
              <ListItemText primary={item.name} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const navBar = (
    <AppBar position='sticky' sx={{ backgroundColor: siteConfig.theme.primaryColor, boxShadow: 'none', py: 2 }} >
      <Stack direction="row"
        justifyContent="space-between"
        sx={{ display: { xs: 'none', lg: 'flex' }, px: { sm: 10, md: 30 } }}>
        <Button sx={{ 
          color: '#fff',
          '@media (prefers-color-scheme: dark)': {
            color: '#ffffff'
          }
        }} href='/'>
          <Typography variant="h5">
            {personalInfo.name}
          </Typography>
        </Button>
        <Stack spacing={8} direction="row"
          justifyContent="space-between">
          {navigationItems.map((item) => (
            <Button key={item.name} sx={{ 
              color: '#fff',
              '@media (prefers-color-scheme: dark)': {
                color: '#ffffff'
              }
            }} href={item.link}>
              <Typography variant="h5">
                {item.name}
              </Typography>
            </Button>
          ))}
        </Stack>
      </Stack >
      <Stack direction="row"
        justifyContent="space-between"
        sx={{ display: { xs: 'flex', sm: 'flex', md: 'flex', lg: 'none' }, justifyContent:'space-between', px: { xs: 3, sm: 4, md: 6 } }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            fontWeight: 600,
            alignSelf: 'center',
            color: 'white',
            '@media (prefers-color-scheme: dark)': {
              color: '#ffffff'
            }
          }}
        >
          {personalInfo.shortName}
        </Typography>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="end"
          onClick={handleDrawerToggle}
          sx={{ 
            p: { xs: 1, sm: 1.5 },
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <MenuIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />
        </IconButton>
      </Stack>
      <Box component="nav">
        <Drawer
          anchor='right'
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: {},
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
    </AppBar >
  );

  function mainContent() {
    return (
      <div>
        <Box sx={{ px: { xs: 2, sm: 4, md: 20  } }}>
          <Stack className='intro-container' sx={{ pt: { xs: 3, md: 5 }, pb: { xs: 1, md: 2 }, alignItems: 'center' }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 2.5, md: 4 }}
              alignItems="center"
              justifyContent="center"
            >
              <Box
                component="img"
                src="/portrait.jpg"
                alt="Michael Ellis"
                sx={{
                  width: { xs: 132, sm: 156, md: 188 },
                  height: { xs: 132, sm: 156, md: 188 },
                  borderRadius: '50%',
                  objectFit: 'cover',
                  flexShrink: 0,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                }}
              />
              <Stack alignItems={{ xs: 'center', md: 'flex-start' }} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Typography sx={{
                  fontSize: '0.8rem',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  color: '#4a5366',
                  '@media (prefers-color-scheme: dark)': { color: '#a5b4fc' }
                }}>
                  {personalInfo.title}
                </Typography>
                <Typography variant='h3' component="h1" sx={{
                  mt: 1,
                  fontSize: { xs: '1.85rem', sm: '2.25rem', md: '2.75rem' },
                  fontWeight: 700,
                  lineHeight: 1.15,
                  color: '#2c3e50',
                  '@media (prefers-color-scheme: dark)': { color: '#ffffff' }
                }}>
                  {personalInfo.headline}
                </Typography>
                <Typography sx={{
                  mt: 1.5,
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  lineHeight: 1.5,
                  color: 'text.secondary',
                  '@media (prefers-color-scheme: dark)': { color: '#b0b0b0' }
                }}>
                  {personalInfo.support}
                </Typography>
                <Button
                  variant="contained"
                  href="#book"
                  sx={{
                    mt: 3,
                    backgroundColor: '#4a5366',
                    color: 'white',
                    px: 3.5,
                    py: 1.25,
                    borderRadius: '25px',
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(74, 83, 102, 0.3)',
                    '@media (prefers-color-scheme: dark)': {
                      backgroundColor: '#6366f1',
                      boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                      '&:hover': { backgroundColor: '#4f46e5' }
                    },
                    '&:hover': {
                      backgroundColor: '#3a4356',
                      boxShadow: '0 6px 20px rgba(74, 83, 102, 0.4)'
                    }
                  }}
                >
                  {personalInfo.heroCta}
                </Button>
              </Stack>
            </Stack>
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 1.5,
              mt: 4,
              width: '100%'
            }}>
              {experience.map((job) => (
                <Box key={job.company} sx={{
                  px: 2,
                  py: 1.25,
                  minWidth: { xs: '140px', sm: '160px' },
                  borderRadius: '14px',
                  backgroundColor: 'white',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                  '@media (prefers-color-scheme: dark)': {
                    backgroundColor: '#1a1a1a',
                    boxShadow: '0 2px 10px rgba(255,255,255,0.06)'
                  }
                }}>
                  <Typography sx={{
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    color: '#2c3e50',
                    '@media (prefers-color-scheme: dark)': { color: '#ffffff' }
                  }}>
                    {job.company}
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.8rem',
                    color: 'text.secondary',
                    lineHeight: 1.4,
                    '@media (prefers-color-scheme: dark)': { color: '#b0b0b0' }
                  }}>
                    {job.role}
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.75rem',
                    color: '#4a5366',
                    fontWeight: 600,
                    mt: 0.25,
                    '@media (prefers-color-scheme: dark)': { color: '#a5b4fc' }
                  }}>
                    {job.dates}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Stack>
          <Box id="work" className='intro-container' sx={{ scrollMarginTop: '88px' }}>
            <Typography variant="h5" sx={{ 
              pt: { xs: 3, sm: 4 },
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
              fontWeight: 600,
              textAlign: 'center',
              color: '#2c3e50',
              '@media (prefers-color-scheme: dark)': {
                color: '#ffffff'
              }
            }}>
              {siteConfig.sections.projects.title}
            </Typography>
          </Box>
          {/* Combined projects - mobile list */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, px: { xs: 2, sm: 4 }, py: { xs: 4, sm: 6 } }}>
            {combinedProjects.map((card) => (
              <Card key={card.name} sx={{
                mb: 2,
                height: { xs: '125px', sm: '135px' },
                borderRadius: '16px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                backgroundColor: 'white',
                overflow: 'hidden',
                '@media (prefers-color-scheme: dark)': {
                  backgroundColor: '#1a1a1a',
                  boxShadow: '0 4px 16px rgba(255,255,255,0.08)'
                }
              }}>
                <CardActionArea href={card.link} sx={{ display: 'flex', alignItems: 'stretch', height: '100%' }}>
                  <Box sx={{
                    width: '100px',
                    height: '100%',
                    backgroundColor: '#e8e8e8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    overflow: 'hidden',
                    '@media (prefers-color-scheme: dark)': { backgroundColor: '#2a2a2a' }
                  }}>
                    {card.imgLink ? (
                      <img src={card.imgLink} alt={card.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: card.imageFit || (card.imgLink.includes('.gif') ? 'contain' : 'cover')
                        }} />
                    ) : (
                      <Typography sx={{ color: '#999', fontSize: '0.75rem', textAlign: 'center', px: 1 }}>
                        Screenshot
                      </Typography>
                    )}
                  </Box>
                  <CardContent sx={{ flex: 1, py: 1.5, px: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 0 }}>
                    <Typography variant="subtitle1" sx={{
                      fontWeight: 700, fontSize: '1rem',
                      color: '#2c3e50', flexShrink: 0,
                      '@media (prefers-color-scheme: dark)': { color: '#fff' }
                    }}>
                      {card.name}
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.85rem', color: 'text.secondary', lineHeight: 1.4, mt: 0.25,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      '@media (prefers-color-scheme: dark)': { color: '#b0b0b0' }
                    }}>
                      {card.description}
                    </Typography>
                    {(card.appstore || card.badges || card.techStack?.length) && (
                      <Box sx={{ display: 'flex', gap: 0.75, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        {card.appstore && (
                          <img src='/appstore.png' alt="App Store"
                            style={{ height: '22px', width: 'auto', borderRadius: '4px' }} />
                        )}
                        {card.badges && (
                          <img src='https://img.shields.io/badge/SPM-compatible-green' alt="SPM"
                            style={{ height: '18px', width: 'auto' }} />
                        )}
                        {card.techStack?.map((tech) => (
                          <Chip key={tech} label={tech} size="small" sx={{
                            height: '22px', fontSize: '0.7rem', fontWeight: 600,
                            backgroundColor: '#e8ecf4', color: '#4a5366',
                            borderRadius: '6px',
                            '@media (prefers-color-scheme: dark)': { backgroundColor: '#333', color: '#ccc' }
                          }} />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
          {/* Combined projects - desktop grid */}
          <Container sx={{ 
            py: { xs: 0, md: 12 },
            px: { xs: 4, sm: 6, md: 8 },
            display: { xs: 'none', md: 'block' }
          }} >
            <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
              {combinedProjects.map((card) => (
                <Grid item key={card.name} xs={12} sm={6} md={6} lg={4} sx={{ display: 'flex' }}>
                  <Card sx={{ 
                    height: '100%', 
                    width: '100%',
                    maxWidth: '100%',
                    backgroundColor: 'white',
                    borderRadius: { xs: '16px', sm: '20px' },
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    transition: 'all 0.3s ease-in-out',
                    overflow: 'hidden',
                    position: 'relative',
                    '@media (prefers-color-scheme: dark)': {
                      backgroundColor: '#1a1a1a',
                      boxShadow: '0 8px 32px rgba(255,255,255,0.1)',
                      '&:hover': {
                        boxShadow: '0 16px 48px rgba(255,255,255,0.15)'
                      }
                    },
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 16px 48px rgba(0,0,0,0.2)'
                    }
                  }}>
                    <CardActionArea 
                      href={card.link}
                      sx={{ 
                        height: '100%',
                        display: 'flex', 
                        flexDirection: 'column'
                      }}
                    >
                      {/* Category Tag - Top Right Corner */}
                      <Box sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        backgroundColor: '#4a5366',
                        color: 'white',
                        px: 2.5,
                        py: 1,
                        borderRadius: '16px',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        zIndex: 10,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        '@media (prefers-color-scheme: dark)': {
                          backgroundColor: '#6366f1',
                          boxShadow: '0 2px 8px rgba(99, 102, 241,0.3)'
                        }
                      }}>
                        {card.category}
                      </Box>
                      {/* Project Image */}
                      <Box sx={{ 
                        position: 'relative',
                        height: { xs: '200px', sm: '250px', md: '300px' },
                        backgroundColor: '#f8f9fa',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        '@media (prefers-color-scheme: dark)': {
                          backgroundColor: '#2a2a2a'
                        }
                      }}>
                        {card.imgLink ? (
                          <img
                            src={card.imgLink}
                            alt={`${card.name}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: card.imageFit || (card.imgLink?.includes('.gif') ? 'contain' : 'cover'),
                              transition: 'transform 0.3s ease-in-out'
                            }}
                            onMouseEnter={(e) => {
                              if (!card.imgLink.includes('.gif') && card.imageFit !== 'contain') {
                                e.target.style.transform = 'scale(1.1)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!card.imgLink.includes('.gif') && card.imageFit !== 'contain') {
                                e.target.style.transform = 'scale(1)';
                              }
                            }}
                          />
                        ) : (
                          <Typography sx={{ 
                            color: '#999',
                            fontSize: '0.95rem',
                            textAlign: 'center',
                            px: 2
                          }}>
                            Add screenshot
                          </Typography>
                        )}
                      </Box>
                      {/* Card Content */}
                      <CardContent sx={{ 
                        flex: 1,
                        p: { xs: 2.5, sm: 3.5 },
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        <Box sx={{ flex: '1 0 auto' }}>
                          <Typography 
                            variant="h5" 
                            sx={{ 
                              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                              fontWeight: 700,
                              mb: 1.5,
                              lineHeight: 1.2,
                              color: '#2c3e50',
                              '@media (prefers-color-scheme: dark)': {
                                color: '#ffffff'
                              }
                            }}
                          >
                            {card.name}
                          </Typography>
                          <Typography 
                            sx={{ 
                              fontSize: { xs: '1rem', sm: '1.1rem' },
                              color: 'text.secondary',
                              lineHeight: 1.6,
                              mb: 2.5,
                              '@media (prefers-color-scheme: dark)': {
                                color: '#b0b0b0'
                              }
                            }}
                          >
                            {card.description}
                          </Typography>
                          {card.techStack?.length > 0 && (
                            <Box sx={{ 
                              display: 'flex', 
                              flexWrap: 'wrap', 
                              gap: 0.75,
                              mb: 2.5,
                              justifyContent: 'center'
                            }}>
                              {card.techStack.map((tech) => (
                                <Chip
                                  key={tech}
                                  label={tech}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#e8ecf4',
                                    color: '#4a5366',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    borderRadius: '8px',
                                    '@media (prefers-color-scheme: dark)': {
                                      backgroundColor: '#333',
                                      color: '#ccc'
                                    }
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ flex: '0 0 auto' }}>
                          {(card.appstore || card.badges) && (
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'center',
                              alignItems: 'center',
                              gap: 2,
                              mb: 1.5,
                              minHeight: '28px'
                            }}>
                              {card.badges && (
                                <img
                                  src='https://img.shields.io/badge/SPM-compatible-green'
                                  alt="Swift Package Manager compatible"
                                  style={{
                                    height: '24px',
                                    width: 'auto',
                                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                                  }}
                                />
                              )}
                              {card.appstore && (
                                <img
                                  src='/appstore.png'
                                  alt="Available on App Store"
                                  style={{
                                    height: '32px',
                                    width: 'auto',
                                    borderRadius: '8px',
                                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                                  }}
                                />
                              )}
                            </Box>
                          )}
                          {/* Action Button */}
                            <Button
                              variant="contained"
                              href={card.link}
                              sx={{
                                width: '100%',
                                backgroundColor: '#4a5366',
                                color: 'white',
                                px: 3.5,
                                py: 1.75,
                                borderRadius: '25px',
                                textTransform: 'none',
                                fontSize: '1rem',
                                fontWeight: 600,
                                height: '48px',
                                whiteSpace: 'nowrap',
                                boxShadow: '0 4px 12px rgba(74, 83, 102, 0.3)',
                                '@media (prefers-color-scheme: dark)': {
                                  backgroundColor: '#6366f1',
                                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                                  '&:hover': {
                                    backgroundColor: '#4f46e5'
                                  }
                                },
                                '&:hover': {
                                  backgroundColor: '#3a4356',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 6px 20px rgba(74, 83, 102, 0.4)'
                                }
                              }}
                            >
                              {card.cta || (card.appstore ? 'View on the App Store' : card.techStack ? 'Visit Site' : 'View Project')}
                            </Button>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
          <Box className='intro-container'>
            <Typography variant="h5" sx={{ 
              pt: { xs: 3, sm: 4 },
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
              fontWeight: 600,
              textAlign: 'center',
              color: '#2c3e50',
              '@media (prefers-color-scheme: dark)': {
                color: '#ffffff'
              }
            }}>
              {siteConfig.sections.services.title}
            </Typography>
          </Box>
          <Container sx={{ 
            py: { xs: 4, sm: 6, md: 8 },
            px: { xs: 4, sm: 6, md: 8 }
          }}>
            <Box sx={{ maxWidth: 880, mx: 'auto', width: '100%' }}>
            <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} justifyContent="center">
              {services.map((service) => (
                <Grid item key={service.title} xs={12} sm={6} sx={{ display: 'flex' }}>
                  <Card sx={{ 
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: 'white',
                    borderRadius: { xs: '16px', sm: '20px' },
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    transition: 'all 0.3s ease-in-out',
                    '@media (prefers-color-scheme: dark)': {
                      backgroundColor: '#1a1a1a',
                      boxShadow: '0 8px 32px rgba(255,255,255,0.1)'
                    },
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 16px 48px rgba(0,0,0,0.2)'
                    }
                  }}>
                    <CardContent sx={{ 
                      flex: 1,
                      p: { xs: 3, sm: 4 },
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center'
                    }}>
                      <Box sx={{ flex: '1 0 auto', pb: 1.5 }}>
                        <Typography variant="h5" sx={{ 
                          fontSize: { xs: '1.2rem', sm: '1.35rem' },
                          fontWeight: 700,
                          mb: 1.5,
                          color: '#2c3e50',
                          '@media (prefers-color-scheme: dark)': {
                            color: '#ffffff'
                          }
                        }}>
                          {service.title}
                        </Typography>
                        <Typography sx={{ 
                          fontSize: { xs: '0.95rem', sm: '1rem' },
                          color: 'text.secondary',
                          lineHeight: 1.6,
                          '@media (prefers-color-scheme: dark)': {
                            color: '#b0b0b0'
                          }
                        }}>
                          {service.description}
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        href="mailto:Contact@MichaelRobertEllis.com"
                        sx={{
                          borderColor: '#4a5366',
                          color: '#4a5366',
                          borderRadius: '25px',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          px: 3,
                          '@media (prefers-color-scheme: dark)': {
                            borderColor: '#6366f1',
                            color: '#a5b4fc'
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(74, 83, 102, 0.08)',
                            borderColor: '#4a5366'
                          }
                        }}
                      >
                        {service.cta}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            </Box>
          </Container>
          <Box id="book" className='intro-container' sx={{ scrollMarginTop: '88px' }}>
            <Typography variant="h5" sx={{ 
              pt: { xs: 3, sm: 4 },
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
              fontWeight: 600,
              textAlign: 'center',
              color: '#2c3e50',
              '@media (prefers-color-scheme: dark)': {
                color: '#ffffff'
              }
            }}>
              Ready to start your project?
            </Typography>
          </Box>
          <Container sx={{ py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 4 } }}>
            <CalendlyInlineWidget />
          </Container>
        </Box>
      </div >
    );
  }

  function PageViewTracker() {
    const location = useLocation();

    useEffect(() => {
      try {
        logEvent(analytics, 'page_view', {
          page_path: location.pathname,
          page_title: document.title,
        });
      } catch (e) {
        // Firebase Analytics unavailable (ad blocker, etc.)
      }
    }, [location]);

    return null;
  }

  return (
    <BrowserRouter>
      <PageViewTracker />
      <Box className="App">
        {navBar}
        <Box className="bgpage" sx={{ 
          borderRadius: { xs: '0px 0px 75px 0px', sm: '0px 0px 175px 0px' },
          '@media (prefers-color-scheme: dark)': {
            backgroundColor: '#0f0f0f'
          }
        }}>
          <Routes>
            <Route path="/" element={<HomeRoute>{mainContent()}</HomeRoute>} />
            <Route path="/encryption-app-privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/white-noise-privacy-policy" element={<WhiteNoisePrivacyPolicy />} />
            <Route path="/white-noise-terms" element={<WhiteNoiseTerms />} />
            <Route path="/white-noise" element={<Hush />} />
            <Route path="/TimeDraw" element={<TimeDraw />} />
            <Route path="/MenuCalendarQuickView" element={<QuickMenuCalendar />} />
            <Route path="/menu-calendar-quick-view-privacy-policy" element={<QuickMenuCalendarPrivacyPolicy />} />
            <Route path="/menu-calendar-quick-view-terms" element={<QuickMenuCalendarTerms />} />
            <Route path="/timedraw-privacy-policy" element={<TimeDrawPrivacyPolicy />} />
            <Route path="/baby-soundboard" element={<BabySoundboard />} />
            <Route path="/baby-soundboard-privacy-policy" element={<BabySoundboardPrivacyPolicy />} />
          </Routes>
        </Box>
        <footer >
          <Stack direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            sx={{ 
              px: { xs: 3, sm: 6, md: 10, lg: 30 }, 
              py: { xs: 3, sm: 4 },
              gap: { xs: 2, sm: 0 }
            }}>
            <Typography sx={{ 
              fontSize: { xs: '0.9rem', sm: '1rem' },
              lineHeight: 1.6,
              textAlign: { xs: 'center', sm: 'left' },
              color: 'white',
              '@media (prefers-color-scheme: dark)': {
                color: '#ffffff'
              }
            }}>
              {footerInfo.left.name} <br /><br />
              <Link
                href={`mailto:${footerInfo.left.email}`}
                sx={{
                  color: 'inherit',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                {footerInfo.left.email}
              </Link>
            </Typography>
            <Typography sx={{ 
              fontSize: { xs: '0.9rem', sm: '1rem' },
              lineHeight: 1.6,
              textAlign: { xs: 'center', sm: 'right' },
              color: 'white',
              '@media (prefers-color-scheme: dark)': {
                color: '#ffffff'
              }
            }}>
             <br /> {footerInfo.right.location}&nbsp;
            </Typography>
          </Stack>
        </footer>
      </Box >
    </BrowserRouter>
  );
}

export default App;
